import React, { useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import CountryMultiSelectFormik from '~/components/core/Formik/CountriesMultiselectFormik/CountryMultiSelectFormik';
import StateMultiselectFormik from '~/components/core/Formik/StatesMultiselectFormik/StateMultiSelectFormik';
import TextField from '~/components/core/Molecules/Fields/TextField';
import EmailTemplateDocumentSelector from '~/components/GenericTemplates/GenericTemplatesConfiguration/EmailTemplateDocumentSelector/EmailTemplateDocumentSelector';
import { TemplateBuilderEditor } from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/TemplateBuilderEditor';
import { TEMPLATE_BUILDER_FORM_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import { MOI_EMAIL_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import TextFieldFormik from '~/components/TextFieldFormik';
import { CoveragesMultiselectFormik } from '~/components/TPA/Coverages/CoveragesMultiselectFormik';
import { LobCheckboxMultiselectFormik } from '~/components/TPA/LOB/LobCheckboxMultiselectFormik';
import SubOrganizationMultiselectFormik from '~/components/TPA/SubOrganizations/SubOrganizationMultiselectFormik';
import useDataFetcher from '~/components/useDataFetcher';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { getOrganizationCountryCode, isFeatureEnabled, isOrganizationWithStates } from '~/Utils';
import { validateNoneEmptyHtmlContent } from '~/Utils/validationUtils';

import MoiEmailMethodsMultiSelectFormik from './MoiEmailMethodsMultiSelectFormik';
import { isMoiEmailTargetFullySelected } from './moiEmailScope';
import MoiEmailTargetGuard from './MoiEmailTargetGuard';
import type { MoiEmailTemplate, MoiEmailTemplateFormValues, MoiMethod, MoiMethodsResponse } from './types';

interface MoiEmailTemplateBuilderContainerProps {
  handleClose: () => void;
  handleSubmit: (values: MoiEmailTemplateFormValues) => Promise<void>;
  initialValues?: MoiEmailTemplateFormValues;
  isEdit?: boolean;
  // All other MOI email templates (excluding the one being edited) — used to disable methods already
  // mapped elsewhere. Includes disabled templates: the backend's scope-uniqueness check ignores
  // is_enabled, so a method owned by a disabled same-scope template would still conflict on save.
  otherTemplates: MoiEmailTemplate[];
}

const MoiEmailTemplateBuilderContainer: React.FC<MoiEmailTemplateBuilderContainerProps> = ({
  handleClose,
  handleSubmit,
  initialValues,
  isEdit = false,
  otherTemplates,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );
  const shouldDisplayStates = isOrganizationWithStates(organization) || multiCountryOrganization;

  const isDefault = !!initialValues?.is_default;

  // The methods picker is only rendered for non-default templates, so skip the fetch on the Default.
  const { data: methodsData, isLoading: isMethodsLoading } = useDataFetcher<MoiMethodsResponse>(
    MOI_EMAIL_TEMPLATES_ROUTES.GET_METHODS(organization.id),
    undefined,
    { shouldFetch: !isDefault }
  );
  const methods: MoiMethod[] = useMemo(() => methodsData?.methods ?? [], [methodsData]);

  // New MOI templates default LOBs, coverages, and states to "all"; sub-org and country are left as an
  // explicit pick (is_all_suborgs / is_all_countries: false) — country seeds to the org's own code for
  // single-country orgs and to an empty pick for multi-country ones, and sub-orgs must be chosen in
  // sub-org orgs. MOI email is Exposure-level only (`is_claim_level: false`); no claim/exposure selector.
  const DEFAULT_FORM_VALUES: MoiEmailTemplateFormValues = {
    is_all_suborgs: false,
    sub_organization_ids: [],
    is_all_lobs: true,
    lobs: [],
    is_all_coverages: true,
    coverage_keys: [],
    countries: multiCountryOrganization ? [] : [getOrganizationCountryCode(organization)],
    is_all_countries: false,
    is_all_states: true,
    states: [],
    is_claim_level: false,
    is_enabled: true,
    template_name: '',
    template_type: 'moi_email',
    body_template: '',
    title_template: '',
    id: null,
    applies_to_moi_method_keys: [],
    document_template_id: null,
  };

  return (
    <CardDialog isDialog title={`${isEdit ? 'Edit ' : ''}MOI Template`} onClose={handleClose} fullWidth maxWidth="md">
      <Formik
        initialValues={initialValues ?? DEFAULT_FORM_VALUES}
        validationSchema={Yup.object().shape({
          [TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_SUBORGS]: Yup.boolean(),
          [TEMPLATE_BUILDER_FORM_KEYS.SUB_ORGANIZATION_IDS]: Yup.array()
            .of(Yup.number())
            .when('is_all_suborgs', {
              is: (is_all_suborgs: boolean) => !isDefault && !is_all_suborgs && organization.sub_organizations_enabled,
              then: Yup.array().of(Yup.number()).min(1, 'At least 1 sub-organization is required'),
            }),
          [TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_LOBS]: Yup.boolean(),
          [TEMPLATE_BUILDER_FORM_KEYS.LOBS]: Yup.array()
            .of(Yup.string())
            .when('is_all_lobs', {
              is: (is_all_lobs: boolean) => !isDefault && !is_all_lobs,
              then: Yup.array().of(Yup.string()).min(1, 'At least 1 line of business is required'),
            }),
          [TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_COVERAGES]: Yup.boolean(),
          [TEMPLATE_BUILDER_FORM_KEYS.COVERAGE_KEYS]: Yup.array()
            .of(Yup.string())
            .when('is_all_coverages', {
              is: (is_all_coverages: boolean) => !isDefault && !is_all_coverages,
              then: Yup.array().of(Yup.string()).min(1, 'At least 1 coverage is required'),
            }),
          [TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_COUNTRIES]: Yup.boolean(),
          [TEMPLATE_BUILDER_FORM_KEYS.COUNTRIES]: Yup.array()
            .of(Yup.string())
            .when('is_all_countries', {
              is: (is_all_countries: boolean) => !isDefault && multiCountryOrganization && !is_all_countries,
              then: Yup.array().of(Yup.string()).min(1, 'Required'),
            }),
          [TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_STATES]: Yup.boolean(),
          [TEMPLATE_BUILDER_FORM_KEYS.STATES]: Yup.array().test(
            'states-required',
            'At least 1 state is required',
            (value, context) => {
              if (isDefault || !shouldDisplayStates) return true;
              const isAllStates: boolean = context.parent[TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_STATES];
              return multiCountryOrganization || isAllStates || (Array.isArray(value) && value.length > 0);
            }
          ),
          document_template_id: Yup.number().nullable(),
          [TEMPLATE_BUILDER_FORM_KEYS.TEMPLATE_NAME]: Yup.string().required('Required'),
          [TEMPLATE_BUILDER_FORM_KEYS.BODY_TEMPLATE]: Yup.string().test(
            'rich-text-none-empty-required',
            'Required',
            validateNoneEmptyHtmlContent
          ),
          // moi_email requires a title (subject).
          [TEMPLATE_BUILDER_FORM_KEYS.TITLE_TEMPLATE]: Yup.string().test(
            'rich-text-none-empty-required',
            'Required',
            validateNoneEmptyHtmlContent
          ),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            // Exposure-level is enforced by the save layer (saveMoiEmailTemplate) — the single source.
            await handleSubmit(values);
            handleClose();
          } catch {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit: formikHandleSubmit, isSubmitting, values }) => {
          // On the Default row the entire target scope is locked to "ALL".
          const scopeDisabled = isDefault || isSubmitting;
          // The LOB/coverage pickers scope their options to the selected sub-orgs (all when is_all_suborgs).
          const scopedSubOrgIds = values.is_all_suborgs
            ? (organization.sub_organizations?.map((subOrg: { id: number }) => subOrg.id) ?? [])
            : values.sub_organization_ids;
          // The methods picker is gated on a fully selected target: method conflicts are keyed by the
          // full scope, so offering methods earlier lets the user pick ones already mapped under the
          // scope they end up choosing. Only dimensions the form shows count.
          const targetIncomplete = !isMoiEmailTargetFullySelected(values, {
            subOrganizations: !!organization.sub_organizations_enabled,
            countries: multiCountryOrganization,
            states: shouldDisplayStates,
          });
          return (
            <>
              <div className="grid grid-cols-2 gap-12">
                <div className="col-span-1 m-4">
                  <TextFieldFormik
                    id={TEMPLATE_BUILDER_FORM_KEYS.TEMPLATE_NAME}
                    label="Template Name"
                    className="mb-2 mt-2"
                    fullWidth
                    disabled={isDefault || isSubmitting}
                  />
                </div>
                {/* Changing the target while scope-dependent values exist (methods / draft /
                    attachment) is confirmed BEFORE the inputs react — Cancel means the change
                    simply never happens. */}
                <MoiEmailTargetGuard disabled={scopeDisabled}>
                  {organization.sub_organizations_enabled ? (
                    <div className="col-span-2 m-4">
                      <SubOrganizationMultiselectFormik
                        subOrganizationsFieldId={TEMPLATE_BUILDER_FORM_KEYS.SUB_ORGANIZATION_IDS}
                        disabled={scopeDisabled}
                        allSelectedFieldId={TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_SUBORGS}
                        selectAllLabel="All"
                        showAllInRenderedSelectedOptions
                        shouldDisplayAllOption
                      />
                    </div>
                  ) : null}
                  <div className="col-span-2 m-4">
                    <Text variant={Text.VARIANTS.SM}>Select line of business</Text>
                    <LobCheckboxMultiselectFormik
                      disabled={scopeDisabled}
                      lobsFieldId={TEMPLATE_BUILDER_FORM_KEYS.LOBS}
                      allSelectedFieldId={TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_LOBS}
                      subOrganizationIds={scopedSubOrgIds}
                    />
                  </div>
                  {/* MOI email templates are Exposure-level only — no claim/exposure selector. */}
                  <div className="col-span-2 m-4">
                    <Text variant={Text.VARIANTS.SM}>Select Coverages (Exposure level)</Text>
                    <CoveragesMultiselectFormik
                      coveragesFieldId={TEMPLATE_BUILDER_FORM_KEYS.COVERAGE_KEYS}
                      lobs={values.is_all_lobs || !values.lobs ? [] : values.lobs}
                      label="Select Coverages"
                      subOrganizationIds={scopedSubOrgIds}
                      disabled={scopeDisabled || values.is_all_suborgs || values.is_all_lobs}
                      disableGeneral
                      allCoveragesFieldId={TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_COVERAGES}
                      showAllInRenderedSelectedOptions
                      shouldDisplayAll
                      isAllOptionChecksAllCheckboxes
                    />
                  </div>
                  {multiCountryOrganization ? (
                    <div className="col-span-2 m-4">
                      <CountryMultiSelectFormik
                        id={TEMPLATE_BUILDER_FORM_KEYS.COUNTRIES}
                        disabled={scopeDisabled}
                        statesFieldIdToResetOnChange={TEMPLATE_BUILDER_FORM_KEYS.STATES}
                        allSelectedFieldId={TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_COUNTRIES}
                        limitTags={3}
                      />
                    </div>
                  ) : null}
                  {shouldDisplayStates ? (
                    <div className="col-span-2 m-4">
                      <StateMultiselectFormik
                        statesFieldId={TEMPLATE_BUILDER_FORM_KEYS.STATES}
                        allSelectedFieldId={TEMPLATE_BUILDER_FORM_KEYS.IS_ALL_STATES}
                        disabled={scopeDisabled || values.is_all_countries}
                        showAllInRenderedSelectedOptions
                        shouldDisplayAllOption
                        country={getOrganizationCountryCode(organization)}
                        isAllOptionChecksAllCheckboxes
                      />
                    </div>
                  ) : null}
                </MoiEmailTargetGuard>
                <div className="col-span-2 m-4">
                  {isDefault ? (
                    <>
                      <Text variant={Text.VARIANTS.SM} className="mb-8">
                        Applies to MOI methods
                      </Text>
                      <TextField value="ALL" disabled fullWidth />
                    </>
                  ) : (
                    <MoiEmailMethodsMultiSelectFormik
                      methods={methods}
                      otherTemplates={otherTemplates}
                      disabled={isSubmitting}
                      targetIncomplete={targetIncomplete}
                      optionsLoading={isMethodsLoading}
                    />
                  )}
                </div>
              </div>

              <div className="m-20 ml-8">
                <EmailTemplateDocumentSelector documentTemplateFieldId="document_template_id" />
              </div>

              <div className="my-2 ml-4 mr-2">
                <TemplateBuilderEditor templateType="moi_email" />
              </div>

              <div className="mt-16 flex w-full justify-end">
                <CancelButton disabled={isSubmitting} onClick={handleClose} />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={() => formikHandleSubmit()}
                >
                  Save
                </Button>
              </div>
            </>
          );
        }}
      </Formik>
    </CardDialog>
  );
};

export default MoiEmailTemplateBuilderContainer;
